import { forwardRef, HTMLAttributes, ReactElement, ReactNode, useEffect, useState } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { Text, Col, Row, Icon, ContentPlaceholder } from '@evertel/web/ui'
import { DepartmentAvatar } from '@evertel/web/department'
import { RoomController } from '@evertel/room'
import { DepartmentController } from '@evertel/department'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataRoomOptions } from '@evertel/stores'

interface ACLRoomTileProps extends HTMLAttributes<HTMLDivElement> {
    roomId: number
    showRoomTypeIcon?: boolean
    showMembersCount?: boolean
    showDepartment?: boolean
    subComponent?: ReactElement
    trailingIcon?: ReactNode
    className?: string
}

const getRoomTypeIcon = (roomOptions?: APIDataRoomOptions, isSearchable?: boolean): IconName => {
    if (isSearchable) {
        return 'bolt'
    } else if (!roomOptions?.departmentMembersCanJoinAsMember) {
        return 'lock'
    } else {
        return 'hashtag'
    }
}

const ACLRoomTile = observer(forwardRef<HTMLDivElement, ACLRoomTileProps>(({
    roomId,
    showRoomTypeIcon = true,
    showMembersCount = false,
    showDepartment = true,
    subComponent,
    trailingIcon,
    className,
    ...otherProps
}, ref) => {

    const sessionService = useService(SessionService, [])
    const roomController = useService(RoomController, [roomId])
    const departmentController = useService(DepartmentController, [roomId])
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        roomController.init(roomId)

        const needsRoomData = !roomController.room
        const needsUserCount = !Number.isInteger(roomController.room?.usersCount)

        if (needsRoomData || needsUserCount) {
            setIsLoading(true)
            const promises = []

            if (needsRoomData) promises.push(roomController.fetchRoom())
            if (needsUserCount) promises.push(roomController.fetchRoomUserCount())

            // eslint-disable-next-line @typescript-eslint/no-empty-function
            Promise.all(promises).catch((err) => {}).finally(() => setIsLoading(false))

        } else {
            setIsLoading(false)
        }
    }, [roomId])

    useEffect(() => {
        if (showDepartment && roomController.room?.departmentId) {
            departmentController.init(
                roomController.room.departmentId as number
            )
        }
    }, [roomController.room?.departmentId])

    const _className = classNames(
        'room-tiles',
        'p-0',
        'd-flex',
        // without this the room name's min-content width wins over the column,
        // overflowing the access dropdown and adding a horizontal scrollbar
        'min-width-0',
        className
    )

    const room = roomController.room
    const department = departmentController.department

    if (isLoading) {
        return (
            <>
                <ContentPlaceholder width="50%" className="mb-1" />
                <ContentPlaceholder width="40%" />
            </>
        )
    }

    return (
        <div
            ref={ref}
            className={_className}
            {...otherProps}>
            <Col className="p-0 mt-2 min-width-0">
                {/* flex row so the name can truncate without pushing the icons out of view */}
                <Text bold className="d-flex align-items-center min-width-0">
                    {(showRoomTypeIcon) &&
                        <Icon
                            name={getRoomTypeIcon(room?.options, room?.isSearchable)}
                            color="body-color"
                            size={12}
                            className="me-1 flex-shrink-0"
                        />
                    }
                    <span className="text-truncate">
                        {room?.name?.trim() ? room.name : `RoomID:${roomId}`}
                    </span>
                    {(trailingIcon) && (
                        <span className="d-inline-flex align-items-center flex-shrink-0">
                            {trailingIcon}
                        </span>
                    )}
                </Text>
                <Row className="align-items-center mt-1 min-width-0">
                    {(showMembersCount) && (
                        <Text
                            size={12}
                            className="me-2 flex-shrink-0">
                            <Icon
                                name="user"
                                size={10}
                                className="me-1"
                            />
                            {(roomController.room?.usersCount)?.toLocaleString()}
                        </Text>
                    )}
                    {showMembersCount && showDepartment && department && '•'}
                    {(showDepartment && department) && (
                        <Text
                            size={12}
                            className="ms-2 text-truncate"
                        >
                            {department?.name}
                        </Text>
                    )}
                </Row>
            </Col>
        </div>
    )
}))

export { ACLRoomTile }
ACLRoomTile.displayName = 'ACLRoomTile'
